import { queryClient } from '../../../ReactQuery';
import useToastedMutation from '../../../ReactQuery/useToastedMutation';
import { put } from '../../../redux/api';

/**
 * Parameters for updating project report custom text.
 */
type UpdateProjectReportCustomTextParams = {
  /** The project locator (e.g., "git+github.com/owner/repo$revision") */
  projectLocator: string;
  /** The custom text content to save for the project report */
  reportCustomText: string;
};

/**
 * Updates the custom text for a specific project's report.
 *
 * @param params - The parameters for updating project report custom text
 * @param params.projectLocator - The project locator string
 * @param params.reportCustomText - The custom text content to save
 * @returns Promise that resolves when the update is complete
 */
async function updateProjectReportCustomText({
  projectLocator,
  reportCustomText,
}: UpdateProjectReportCustomTextParams): Promise<void> {
  await put(`/api/projects/${projectLocator}`, { reportCustomText });
}

/**
 * Hook for updating project report custom text.
 *
 * This hook provides a mutation function to update the custom text content
 * for a specific project's report. It handles success/error messaging and
 * invalidates relevant queries on success.
 *
 * @example
 * ```typescript
 * const { mutateAsync } = useUpdateProjectReportCustomText();
 *
 * await mutateAsync({
 *   projectLocator: "git+github.com/owner/repo$revision",
 *   reportCustomText: "Custom header text for this project"
 * });
 * ```
 *
 * @returns A React Query mutation object with mutateAsync function
 */
export default function useUpdateProjectReportCustomText() {
  return useToastedMutation({
    mutationFn: updateProjectReportCustomText,
    successToastMessage: 'Project report text updated successfully',
    errorToastMessage: 'Failed to update project report text',
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organization'] });
    },
  });
}
